import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import type maplibregl from 'maplibre-gl'
import { cabinetApi } from '../api'
import type { BBox } from '../api'
import { isobathZoomStep } from './isobaths'

/** Минимум, который хук просит у карты — как у рубежей ИЭЗ и по той же
 *  причине: логика «что просить и когда» обязана остаться одной на все карты. */
export interface DistrictMapSink {
  getMap: () => maplibregl.Map | null
  getBBox: () => BBox
  setDistricts: (data: GeoJSON.FeatureCollection) => void
  removeDistricts: () => void
}

/**
 * Оверлей «Промысловые районы».
 *
 * Даты у слоя нет: границы меняются приказом раз в годы. Зато есть ВЬЮПОРТ —
 * геометрия режется и упрощается по кадру, иначе весь Дальний Восток приезжал
 * бы на каждом зуме (замер: 114 КБ на z4).
 *
 * Дебаунс тот же, что у изобат и рубежей: пан и инерция зума сыплют `moveend`.
 */
const DEBOUNCE_MS = 250

export function useDistrictsLayer(
  mapRef: RefObject<DistrictMapSink | null>,
  enabled: boolean,
  viewKey: string,
): { attribution: string | null; disclaimerKey: string | null } {
  // Подпись показывается, ТОЛЬКО когда слой что-то нарисовал: оговорка
  // «построено нами, не навигационный документ» относится к показанным
  // границам, а над пустой картой обещала бы границы, которых в кадре нет.
  const [meta, setMeta] = useState<{ attribution: string; disclaimerKey: string } | null>(null)

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!enabled) {
      map.removeDistricts()
      setMeta(null)
      return
    }
    let alive = true
    const id = setTimeout(() => {
      const zoom = isobathZoomStep(map.getMap()?.getZoom() ?? 6)
      cabinetApi
        .fishingDistricts({ bbox: map.getBBox(), zoom })
        .then(fc => {
          if (!alive) return
          if (!fc.features?.length) {
            // Вне рамки построения районов нет — это не сбой, но и чужие
            // границы висеть не должны.
            map.removeDistricts()
            setMeta(null)
            return
          }
          map.setDistricts(fc as unknown as GeoJSON.FeatureCollection)
          setMeta(
            fc.meta
              ? { attribution: fc.meta.attribution, disclaimerKey: fc.meta.disclaimer_key }
              : null,
          )
        })
        .catch(() => {
          if (!alive) return
          map.removeDistricts()
          setMeta(null)
        })
    }, DEBOUNCE_MS)
    return () => {
      alive = false
      clearTimeout(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, viewKey])

  useEffect(
    () => () => mapRef.current?.removeDistricts(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return {
    attribution: meta?.attribution ?? null,
    disclaimerKey: meta?.disclaimerKey ?? null,
  }
}
